import { DocumentServicesObject } from '@wix/document-services-types';
import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import { IEditorPlatformPackagesBridge } from '@wix/editor-platform-interfaces';
import { sdkEventHandlersStore } from '../sdkEventHandlersStore/sdkEventHandlersStore';
import { platformEvents } from '../../../../../platformEvents';

export interface AppRefreshService {
  addAppToRefresh: (appDefinitionIds: string[]) => Promise<void>;
  refreshVisitedApps: () => Promise<void>;
  refreshRouters: (options?: RouterRefreshOptions) => void;
}

let resolveAppRefreshServices: (value: AppRefreshService) => void;

export type RouterRefreshOptions = {
  appDefinitionId?: string;
  selectedRoute?: string;
};

export const buildRouterRefreshPayload = (options?: RouterRefreshOptions) => ({
  ...(options?.appDefinitionId !== undefined
    ? { appDefinitionId: options.appDefinitionId }
    : {}),
  ...(options?.selectedRoute !== undefined
    ? { selectedRoute: options.selectedRoute }
    : {}),
});

const appRefreshServiceServicesPromise = new Promise<AppRefreshService>(
  (resolve) => {
    resolveAppRefreshServices = resolve;
  },
);

export async function getAppRefreshServices() {
  return appRefreshServiceServicesPromise;
}

export const createAppRefreshService = (
  documentServicesAPI: DocumentServicesObject,
  editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge>,
  platformApiMethods: PlatformApiMethods,
): AppRefreshService => {
  const refreshRouters = (options?: RouterRefreshOptions) => {
    editorPlatformPackagesBridge.PlatformEventsEmitter?.emit(
      'routers.refreshed',
      buildRouterRefreshPayload(options),
    );
  };

  const refreshAppComps = (appDefinitionIds: string[]) => {
    const appComps =
      documentServicesAPI.tpaV2.app.getAllComps(appDefinitionIds);
    // @ts-expect-error TBD fix
    documentServicesAPI.tpa.app.refreshApp(appComps);
  };

  const refreshAppsLivePreview = (appDefinitionIds: string[]) => {
    documentServicesAPI.platform.livePreview.refresh({
      apps: appDefinitionIds,
      source: 'editorRefreshAppComps',
      shouldFetchData: false,
    });
  };

  const addAppToRefresh = async (appDefinitionIds: string[]) => {
    const appIds = appDefinitionIds.filter(
      (appDefinitionId) =>
        documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId) !==
        undefined,
    );

    sdkEventHandlersStore.setAppsToRefresh(appIds);

    const BUSINESS_MANAGER_APP_ID = 'business-manager-general-info';

    if (appDefinitionIds.includes(BUSINESS_MANAGER_APP_ID)) {
      sdkEventHandlersStore.setShouldRefreshGeneralInfo(true);
    }
  };

  const notifyAppsVisited = (appDefinitionIds: string[]) => {
    documentServicesAPI.platform.notifyAppsOnCustomEvent(
      platformEvents.factory.appVisitedInDashboard({
        visitedAppDefIds: appDefinitionIds,
      }),
    );
  };

  const refreshVisitedApps = async () => {
    const { appsToRefresh, shouldRefreshGeneralInfo } = sdkEventHandlersStore;

    try {
      if (appsToRefresh.length) {
        refreshAppComps(appsToRefresh);
        refreshAppsLivePreview(appsToRefresh);
        notifyAppsVisited(appsToRefresh);
        refreshRouters();
        if (appsToRefresh.includes('d90652a2-f5a1-4c7c-84c4-d4cdcc41f130')) {
          documentServicesAPI.components.runtime.updateProps(
            [
              platformApiMethods.editor.selection.getSingleSelectedOrThrow()
                .documentPointer.id,
            ],
            {
              timeStamp: Date.now().toString(),
            },
          );
        }
        sdkEventHandlersStore.clearAppsToRefresh();
      }

      if (shouldRefreshGeneralInfo) {
        // @ts-expect-error - The method is not defined in the type definition
        documentServicesAPI.generalInfo.sitePropertiesInfo.reload();
        sdkEventHandlersStore.setShouldRefreshGeneralInfo(false);
      }
    } catch (error) {
      console.error('Failed to refresh app:', error);
    }
  };

  return {
    addAppToRefresh,
    refreshVisitedApps,
    refreshRouters,
  };
};

export const initAppRefreshService = (
  documentServicesAPI: DocumentServicesObject,
  editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge>,
  platformApiMethods: PlatformApiMethods,
) => {
  const appRefreshService = createAppRefreshService(
    documentServicesAPI,
    editorPlatformPackagesBridge,
    platformApiMethods,
  );

  resolveAppRefreshServices(appRefreshService);

  return appRefreshService;
};
